import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import ContentEditModal from '../ContentEditModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SettingsSection({ onSettingsUpdate }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [showContentEdit, setShowContentEdit] = useState(false);
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/admin/settings');
      const data = response.data;
      
      // Helper function to convert URLs to use current backend
      const fixImageUrl = (url) => {
        if (!url) return url;
        
        // If relative URL, add current backend
        if (!url.startsWith('http')) {
          return `${BACKEND_URL}${url}`;
        }
        
        // If absolute URL but from old domain, extract path and use current backend
        if (url.includes('/api/images/view/') || url.includes('/uploads/')) {
          // Extract the path part (everything after the domain)
          const pathMatch = url.match(/\/(api\/images\/view\/.*|uploads\/.*)/);          if (pathMatch) {
            return `${BACKEND_URL}/${pathMatch[1]}`;
          }
        }
        
        return url;
      };
      
      // Fix logo and icon URLs
      data.site_logo_url = fixImageUrl(data.site_logo_url);
      data.site_icon_url = fixImageUrl(data.site_icon_url);
      setSettings(data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      console.log('💾 Saving settings:', settings);
      await axios.put('/admin/settings', settings);
      toast.success('✅ Settings saved successfully!');
      console.log('✅ Settings saved to database');
      
      // Notify parent to refetch site settings
      if (onSettingsUpdate) {
        console.log('🔄 Refetching site settings...');
        await onSettingsUpdate();
      }
    } catch (error) {
      console.error('❌ Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post('/admin/settings/upload-logo', formData);
      setSettings(prev => ({ ...prev, site_logo_url: `${BACKEND_URL}${response.data.image_url}` }));
      toast.success('✅ Logo uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleIconUpload = async (file) => {
    if (!file) return;
    setUploadingIcon(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post('/admin/settings/upload-icon', formData);
      setSettings(prev => ({ ...prev, site_icon_url: `${BACKEND_URL}${response.data.image_url}` }));
      toast.success('✅ Icon uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload icon:', error);
      toast.error('Failed to upload icon');
    } finally {
      setUploadingIcon(false);
    }
  };

  if (!settings) {
    return <div className="text-center py-8">Loading settings...</div>;
  }

  const timezones = [
    "Australia/Sydney", "Australia/Melbourne", "Australia/Brisbane",
    "Australia/Perth", "Australia/Adelaide", "Australia/Darwin",
    "Australia/Hobart", "Pacific/Auckland", "Pacific/Fiji",
    "America/New_York", "America/Los_Angeles", "America/Chicago",
    "Europe/London", "Europe/Paris", "Asia/Tokyo", "Asia/Singapore"
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Site Settings</h2>
          <p className="text-gray-600">Configure your site settings and preferences</p>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={() => setShowContentEdit(true)} 
            variant="outline"
            className="border-orange-500 text-custom-orange hover:bg-orange-50"
          >
            📝 Content Edit
          </Button>
          <Button 
            onClick={() => setShowPaymentsModal(true)} 
            variant="outline"
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
          >
            💳 Payments
          </Button>
          <Button 
            onClick={handleSaveSettings} 
            disabled={loading}
            className="bg-custom-orange hover:bg-custom-orange"
          >
            {loading ? 'Saving...' : 'Save All Settings'}
          </Button>
        </div>
      </div>

      <Card className="p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">General Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site Title</label>
            <input
              type="text"
              value={settings.site_title}
              onChange={(e) => setSettings(prev => ({ ...prev, site_title: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site Tagline</label>
            <input
              type="text"
              value={settings.site_tagline}
              onChange={(e) => setSettings(prev => ({ ...prev, site_tagline: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>
      </Card>

      <Card className="p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Site Images</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site Logo</label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
              onChange={(e) => handleLogoUpload(e.target.files[0])}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              disabled={uploadingLogo}
            />
            <p className="text-xs text-gray-500 mt-1">Accepted formats: PNG, JPG, WebP, GIF</p>
            {uploadingLogo && <p className="text-sm text-blue-600 mt-1">Uploading...</p>}
            {settings.site_logo_url && (
              <div className="mt-2">
                <img 
                  src={settings.site_logo_url} 
                  alt="Logo Preview" 
                  className="w-32 h-auto border rounded"
                  onError={(e) => console.error('Logo preview failed:', settings.site_logo_url)}
                />
                <p className="text-xs text-gray-600 mt-1">Current logo</p>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site Icon (Favicon)</label>
            <input
              type="file"
              accept="image/x-icon,image/png,image/webp"
              onChange={(e) => handleIconUpload(e.target.files[0])}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              disabled={uploadingIcon}
            />
            <p className="text-xs text-gray-500 mt-1">Accepted formats: ICO, PNG, WebP (recommended: 32x32px)</p>
            {uploadingIcon && <p className="text-sm text-blue-600 mt-1">Uploading...</p>}
            {settings.site_icon_url && (
              <div className="mt-2">
                <img 
                  src={settings.site_icon_url} 
                  alt="Favicon Preview" 
                  className="w-8 h-8 border rounded"
                  onError={(e) => console.error('Favicon preview failed:', settings.site_icon_url)}
                />
                <p className="text-xs text-gray-600 mt-1">Current favicon</p>
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Site Status</h3>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <input
              type="checkbox"
              checked={settings.site_online}
              onChange={(e) => setSettings(prev => ({ ...prev, site_online: e.target.checked }))}
              className="w-5 h-5"
            />
            <label className="text-sm font-medium text-gray-700">Site Online</label>
          </div>
          {!settings.site_online && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Offline Message</label>
              <textarea
                value={settings.offline_message}
                onChange={(e) => setSettings(prev => ({ ...prev, offline_message: e.target.value }))}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          )}
        </div>
      </Card>

      <Card className="p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Settings</h3>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Order Amount (Subtotal)</label>
          <div className="flex items-center gap-2">
            <span className="text-gray-600">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={settings.minimum_order_amount || 0}
              onChange={(e) => setSettings(prev => ({ ...prev, minimum_order_amount: parseFloat(e.target.value) || 0 }))}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
            />
            <span className="text-gray-600">AUD</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">Set to $0.00 to allow any order amount. Applies to subtotal before shipping.</p>
        </div>
      </Card>

      <Card className="p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Image Upload Settings</h3>
        
        {/* File Formats */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Allowed File Formats</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            {['jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'tiff', 'tif', 'gif', 'bmp'].map(format => (
              <label key={format} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={(settings.allowed_file_formats || []).includes(format)}
                  onChange={(e) => {
                    const formats = settings.allowed_file_formats || [];
                    if (e.target.checked) {
                      setSettings(prev => ({ 
                        ...prev, 
                        allowed_file_formats: [...formats, format] 
                      }));
                    } else {
                      setSettings(prev => ({ 
                        ...prev, 
                        allowed_file_formats: formats.filter(f => f !== format) 
                      }));
                    }
                  }}
                  className="w-4 h-4"
                />
                <span className="text-sm uppercase">.{format}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Add custom format (e.g., raw)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  const format = e.target.value.trim().toLowerCase().replace('.', '');
                  if (format && !(settings.allowed_file_formats || []).includes(format)) {
                    setSettings(prev => ({ 
                      ...prev, 
                      allowed_file_formats: [...(prev.allowed_file_formats || []), format] 
                    }));
                    e.target.value = '';
                  }
                }
              }}
            />
            <span className="text-xs text-gray-500 self-center">Press Enter to add</span>
          </div>
        </div>

        {/* File Size Limits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Min File Size (MB)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={settings.min_file_size_mb || 0.01}
              onChange={(e) => setSettings(prev => ({ ...prev, min_file_size_mb: parseFloat(e.target.value) || 0.01 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            <p className="text-xs text-gray-500 mt-1">Recommended: 0.01 MB (10KB)</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max File Size (MB)</label>
            <input
              type="number"
              step="1"
              min="1"
              value={settings.max_file_size_mb || 50}
              onChange={(e) => setSettings(prev => ({ ...prev, max_file_size_mb: parseFloat(e.target.value) || 50 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            <p className="text-xs text-gray-500 mt-1">Recommended: 50 MB</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Max Total Upload (MB)</label>
            <input
              type="number"
              step="10"
              min="10"
              value={settings.max_total_upload_mb || 500}
              onChange={(e) => setSettings(prev => ({ ...prev, max_total_upload_mb: parseFloat(e.target.value) || 500 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            <p className="text-xs text-gray-500 mt-1">Recommended: 500 MB</p>
          </div>
        </div>

        {/* DPI Limits */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Minimum DPI</label>
            <input
              type="number"
              step="1"
              min="1"
              value={settings.min_dpi || 72}
              onChange={(e) => setSettings(prev => ({ ...prev, min_dpi: parseInt(e.target.value) || 72 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            <p className="text-xs text-gray-500 mt-1">Recommended: 72 DPI (web quality)</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Maximum DPI</label>
            <input
              type="number"
              step="50"
              min="72"
              value={settings.max_dpi || 600}
              onChange={(e) => setSettings(prev => ({ ...prev, max_dpi: parseInt(e.target.value) || 600 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            <p className="text-xs text-gray-500 mt-1">Recommended: 600 DPI (high quality print)</p>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Timezone Settings</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
            <select
              value={settings.timezone}
              onChange={(e) => setSettings(prev => ({ ...prev, timezone: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            >
              {timezones.map(tz => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
            <p className="text-xs text-gray-600 mt-2 p-2 bg-blue-50 rounded">
              ℹ️ Current time in <strong>{settings.timezone}</strong>: {' '}
              <strong>{new Date().toLocaleString('en-AU', { 
                timeZone: settings.timezone,
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true,
                timeZoneName: 'short'
              })}</strong>
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.daylight_savings}
                onChange={(e) => setSettings(prev => ({ ...prev, daylight_savings: e.target.checked }))}
                className="w-5 h-5"
              />
              <label className="text-sm font-medium text-gray-700">Daylight Savings Active</label>
            </div>
            <p className="text-xs text-gray-500 mt-1 ml-7">
              Note: JavaScript automatically handles DST transitions. This checkbox is informational only.
            </p>
          </div>
        </div>
      </Card>

      {/* Content Edit Modal */}
      <ContentEditModal 
        isOpen={showContentEdit} 
        onClose={() => setShowContentEdit(false)} 
      />
      
      {/* Payments Settings Modal */}
      <Dialog open={showPaymentsModal} onOpenChange={setShowPaymentsModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payment Settings</DialogTitle>
            <DialogDescription>
              Configure payment methods for your store
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Stripe Settings */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings?.stripe_enabled || false}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        stripe_enabled: e.target.checked,
                        payment_providers: e.target.checked 
                          ? [...(prev.payment_providers || []).filter(p => p !== 'stripe'), 'stripe']
                          : (prev.payment_providers || []).filter(p => p !== 'stripe')
                      }));
                    }}
                    className="w-5 h-5"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900">Stripe</h3>
                    <p className="text-sm text-gray-500">Accept credit cards via Stripe</p>
                  </div>
                </div>
                {settings?.stripe_enabled && (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings?.stripe_sandbox_mode || false}
                      onChange={(e) => setSettings(prev => ({ ...prev, stripe_sandbox_mode: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Sandbox Mode</span>
                  </label>
                )}
              </div>
              
              {settings?.stripe_enabled && (
                <div className="space-y-3 mt-4 pl-8">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Publishable Key {settings?.stripe_sandbox_mode && <span className="text-xs text-gray-500">(Test)</span>}
                    </label>
                    <input
                      type="text"
                      placeholder="pk_test_..."
                      value={settings?.stripe_public_key || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, stripe_public_key: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secret Key {settings?.stripe_sandbox_mode && <span className="text-xs text-gray-500">(Test)</span>}
                    </label>
                    <input
                      type="password"
                      placeholder="sk_test_..."
                      value={settings?.stripe_secret_key || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, stripe_secret_key: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Get your keys from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Stripe Dashboard</a>
                  </p>
                </div>
              )}
            </Card>

            {/* Square Settings */}
            <Card className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings?.square_enabled || false}
                    onChange={(e) => {
                      setSettings(prev => ({
                        ...prev,
                        square_enabled: e.target.checked,
                        payment_providers: e.target.checked 
                          ? [...(prev.payment_providers || []).filter(p => p !== 'square'), 'square']
                          : (prev.payment_providers || []).filter(p => p !== 'square')
                      }));
                    }}
                    className="w-5 h-5"
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900">Square</h3>
                    <p className="text-sm text-gray-500">Accept payments via Square</p>
                  </div>
                </div>
                {settings?.square_enabled && (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings?.square_sandbox_mode || false}
                      onChange={(e) => setSettings(prev => ({ ...prev, square_sandbox_mode: e.target.checked }))}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Sandbox Mode</span>
                  </label>
                )}
              </div>
              
              {settings?.square_enabled && (
                <div className="space-y-3 mt-4 pl-8">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Application ID {settings?.square_sandbox_mode && <span className="text-xs text-gray-500">(Sandbox)</span>}
                    </label>
                    <input
                      type="text"
                      placeholder="sandbox-sq0idb-..."
                      value={settings?.square_app_id || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, square_app_id: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Access Token {settings?.square_sandbox_mode && <span className="text-xs text-gray-500">(Sandbox)</span>}
                    </label>
                    <input
                      type="password"
                      placeholder="EAAAl..."
                      value={settings?.square_access_token || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, square_access_token: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Get your credentials from <a href="https://developer.squareup.com/apps" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Square Developer Dashboard</a>
                  </p>
                </div>
              )}
            </Card>

            {/* Test Mode */}
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={settings?.test_mode_enabled || false}
                  onChange={(e) => {
                    setSettings(prev => ({
                      ...prev,
                      test_mode_enabled: e.target.checked,
                      payment_providers: e.target.checked 
                        ? [...(prev.payment_providers || []).filter(p => p !== 'test_mode'), 'test_mode']
                        : (prev.payment_providers || []).filter(p => p !== 'test_mode')
                    }));
                  }}
                  className="w-5 h-5"
                />
                <div>
                  <h3 className="font-semibold text-gray-900">Test Mode</h3>
                  <p className="text-sm text-gray-500">Skip payment processing for testing (orders marked as paid)</p>
                </div>
              </div>
            </Card>
          </div>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowPaymentsModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                handleSaveSettings();
                setShowPaymentsModal(false);
              }}
              className="bg-custom-orange hover:bg-custom-orange"
            >
              Save Payment Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SettingsSection;